import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

export interface AdminV2MessagingEmailSignature {
  id: string;
  organisationId: null;
  name: string;
  role: string | null;
  photoUrl: string | null;
  version: number;
}

export interface AdminV2MessagingEmailSignatureInput {
  organisationId: null;
  name: string;
  role: string | null;
  photoUrl: string | null;
}

export interface MessagingManualSignatureSelection {
  signatureId: string;
  signatureVersion: number;
}

export interface MessagingManualSignatureDefault {
  actorStaffUserId: string;
  version: number;
  state: "team_fallback" | "selected" | "unavailable";
  reason: "not_selected" | "signature_missing" | "signature_changed" | null;
  selection: MessagingManualSignatureSelection | null;
  signature: AdminV2MessagingEmailSignature | null;
}

export interface MessagingManualSignatureDefaultInput {
  expectedVersion: number;
  selection: MessagingManualSignatureSelection | null;
}

export interface MessagingSignatureCatalogue {
  signatures: AdminV2MessagingEmailSignature[];
  branding: { loginUrl: string | null };
  manualDefault: MessagingManualSignatureDefault;
}

const ROOT = "/admin/v2/messaging/settings/email-signatures";
const signaturesTag = {
  type: "AdminV2MessagingSettings" as const,
  id: "EMAIL_SIGNATURES",
};
const invalidatesTags = [
  signaturesTag,
  { type: "AdminV2AuditLog" as const, id: "LIST" },
];
const fields = (
  input: AdminV2MessagingEmailSignatureInput,
): AdminV2MessagingEmailSignatureInput => ({
  organisationId: null,
  name: input.name,
  role: input.role,
  photoUrl: input.photoUrl,
});

/** App-wide Signature CRUD on the existing authenticated API slice. */
export const buildAdminV2MessagingSignatureEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2MessagingEmailSignatures: builder.query<
    MessagingSignatureCatalogue,
    { ownerKey: string } | void
  >({
    query: () => ({ url: ROOT }),
    providesTags: [signaturesTag],
    keepUnusedDataFor: 0,
  }),
  updateAdminV2MessagingManualSignatureDefault: builder.mutation<
    { manualDefault: MessagingManualSignatureDefault },
    MessagingManualSignatureDefaultInput
  >({
    query: ({ expectedVersion, selection }) => ({
      url: `${ROOT}/manual-default`,
      method: "PUT",
      data: {
        expectedVersion,
        selection: selection
          ? {
              signatureId: selection.signatureId,
              signatureVersion: selection.signatureVersion,
            }
          : null,
      },
    }),
    invalidatesTags,
  }),
  createAdminV2MessagingEmailSignature: builder.mutation<
    AdminV2MessagingEmailSignature,
    AdminV2MessagingEmailSignatureInput
  >({
    query: (input) => ({ url: ROOT, method: "POST", data: fields(input) }),
    invalidatesTags,
  }),
  updateAdminV2MessagingEmailSignature: builder.mutation<
    AdminV2MessagingEmailSignature,
    AdminV2MessagingEmailSignatureInput & {
      signatureId: string;
      expectedVersion: number;
    }
  >({
    query: ({ signatureId, expectedVersion, ...input }) => ({
      url: `${ROOT}/${encodeURIComponent(signatureId)}`,
      method: "PUT",
      data: { ...fields(input), expectedVersion },
    }),
    invalidatesTags,
  }),
  deleteAdminV2MessagingEmailSignature: builder.mutation<
    { deleted: true },
    { signatureId: string; expectedVersion: number }
  >({
    query: ({ signatureId, expectedVersion }) => ({
      url: `${ROOT}/${encodeURIComponent(signatureId)}`,
      method: "DELETE",
      data: { expectedVersion },
    }),
    invalidatesTags,
  }),
});
